/**
 * The step graph, as pure functions. THE PLAN, §4 (delivery 1b and 1c).
 *
 * IT IS THE ONLY TASK DRAFT LEFT. It succeeded `task-draft.js` — the free-text editor's,
 * deleted with that editor — and a task is a step graph now: `TaskComposePage` writes one
 * from its first save and `POST /v1/scenarios` refuses a create without one. A stored
 * `definition` of `null` is still a real answer on the wire (a legacy row), and the reader
 * still carries it as one; there is simply no longer a second editor behind it.
 *
 * ── THE ONE RULE THIS FILE EXISTS TO KEEP ────────────────────────────────────────────
 * A DEFINITION WRITTEN BY A NEWER SIDECAR MUST SURVIVE AN OLDER BUILDER. The sidecar owns
 * the shape (`services/ai-sidecar/src/tasks/types.ts`); the builder is one of its readers
 * and will always be the LAST to learn about a new field. A builder that read a step into
 * named variables and wrote those variables back out would silently delete every field it
 * was too old to know about — and the owner's only clue would be a task that stopped
 * working after she renamed it.
 *
 * So a row keeps `source`: the step object exactly as it arrived. `stepToWire` starts from
 * a copy of it and overwrites only the fields this builder actually edits. Unknown fields
 * ride along untouched. A step of an unknown TYPE is not edited at all — the card shows it
 * read-only and says so — because its unknown fields are the whole step.
 *
 * ── TWO SHAPES, AND THE BOUNDARY BETWEEN THEM ───────────────────────────────────────
 *   • THE DRAFT is what the cards bind to: rows with local `key`s (a `v-for` keyed on the
 *     index drops an in-flight Arabic composition when a step moves), a `rule` flattened
 *     into a mode plus its fields, `inputs`/`keep` as arrays rather than records, and
 *     `source` as above.
 *   • THE WIRE SHAPE is what `taskDefinitionSchema` parses. Nothing outside this file
 *     builds one, and the page's dirty check compares wire against wire — so adding a step
 *     and removing it again leaves the page clean.
 *
 * ── AND THE SECOND RULE ──────────────────────────────────────────────────────────────
 * A STEP'S OPTION `value` IS NEVER SHOWN TO A CUSTOMER. Values are internal ids — a row id
 * from the owner's own system — and everything customer-facing here reads a label.
 */

import { IDENTITY_PARAM } from './connection-draft.js';
import { optionLanguageProblems } from './missing-language.js';
import { toIntlLocale } from './intl.js';
import { validHandTo, validHandoverLabels } from './hand-to.js';

/** Row keys are local to one editing session and never leave the browser. */
let nextKey = 0;
const localKey = () => {
  nextKey += 1;
  return `r${nextKey}`;
};

// ── the sidecar's own bounds, mirrored ────────────────────────────────────────────────
// Every number here is `services/ai-sidecar/src/tasks/schema.ts`. They are mirrored rather
// than guessed so the counter under a box and the refusal from the server say the same
// thing; the server remains the one that is load bearing.
export const MAX_GRAPH_STEPS = 30;
export const MAX_CHOOSE_OPTIONS = 10;
/** Field fixes G2.3: a question they may pick more than one answer to (`MAX_MANY_OPTIONS`). */
export const MAX_MANY_OPTIONS = 20;
/**
 * How many choices a question may hold: twenty when they may pick more than one (`step.many`),
 * ten otherwise. The sidecar's choose-step `.refine`; the Add button and `shapeProblems` both
 * read it, so the composer never offers a choice the page then refuses to save.
 */
export const optionCapOf = many =>
  many ? MAX_MANY_OPTIONS : MAX_CHOOSE_OPTIONS;
export const MAX_OPTION_LABEL = 20;
export const MAX_INSTRUCTION = 1000;
export const MAX_NAME = 60;
export const MAX_NOTE = 500;
export const MAX_EXACT = 1000;

/** P-4.1: WhatsApp's limit on a link button's words, and on its address. */
export const MAX_LINK_LABEL = 20;
export const MAX_LINK_URL = 2000;

/** P-4.1: the sidecar's `isSendableLink`: https, a host, at most 2000 characters, no whitespace. */
export const isSendableLink = url => {
  const value = typeof url === 'string' ? url : '';
  if (value === '' || value.length > MAX_LINK_URL || /\s/.test(value))
    return false;
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'https:' && parsed.hostname !== '';
  } catch {
    return false;
  }
};

/** `stepId` in the schema: what a step id and an option id must both look like. */
const STEP_ID = /^[A-Za-z0-9_-]{1,32}$/;
/** `saveKey`: snake_case, because it is also a connection input's name. */
const SAVE_KEY = /^[a-z][a-z0-9_]{0,31}$/;

export const STEP_TYPES = [
  'ask',
  'choose',
  'choose_live',
  'call',
  'tell',
  'handoff',
  'end',
];
export const ANSWER_KINDS = [
  'text',
  'number',
  'date',
  'time',
  'email',
  'phone',
  // Services field fixes G2.4: what the customer SENDS (`helpers/task-uploads.js`).
  'photo',
  'file',
];
export const LIVE_FORMATS = ['time', 'date', 'price', 'text'];
export const RULE_MODES = ['none', 'range', 'allowed', 'pattern'];

export const isKnownType = type => STEP_TYPES.includes(type);

// ── reading a definition in ───────────────────────────────────────────────────────────

const str = value => (typeof value === 'string' ? value : '');
const rec = value =>
  value && typeof value === 'object' && !Array.isArray(value) ? value : {};

/** `{min,max} | {allowed} | {pattern} | undefined` flattened into one editable row. */
const toRule = rule => {
  const row = {
    mode: 'none',
    min: '',
    max: '',
    allowed: [],
    pattern: '',
  };
  if (!rule || typeof rule !== 'object') return row;
  if ('allowed' in rule) {
    return {
      ...row,
      mode: 'allowed',
      allowed: Array.isArray(rule.allowed) ? rule.allowed.map(str) : [],
    };
  }
  if ('pattern' in rule) {
    return { ...row, mode: 'pattern', pattern: str(rule.pattern) };
  }
  if ('min' in rule || 'max' in rule) {
    return {
      ...row,
      mode: 'range',
      min: typeof rule.min === 'number' ? String(rule.min) : '',
      max: typeof rule.max === 'number' ? String(rule.max) : '',
    };
  }
  return row;
};

/** `{ chat?, contact? }` — two boxes, and the schema refuses one that saves nowhere. */
const toSaveTo = saveTo => ({
  chat: str(rec(saveTo).chat),
  contact: str(rec(saveTo).contact),
});

/**
 * Where an input comes from: `key` is an answer saved earlier, `value` is a fixed string the
 * owner typed, and `lang` (services field report F2.3) is the chat's language, which Waslo sends
 * as "ar" or "en". Only the literal `{ lang: true }` is the language, exactly as the sidecar's
 * schema reads it.
 */
const inputMode = source => {
  if (source.lang === true) return 'lang';
  return 'key' in source ? 'key' : 'value';
};

/** `Record<name, {key}|{value}|{lang}>` → rows, so a rename does not re-key the whole object. */
const toInputs = inputs =>
  Object.entries(rec(inputs)).map(([name, source]) => ({
    key: localKey(),
    name,
    mode: inputMode(rec(source)),
    from: str(rec(source).key),
    value: str(rec(source).value),
  }));

/** `Record<resultField, chatKey>` → rows. */
const toKeep = keep =>
  Object.entries(rec(keep)).map(([field, saveAs]) => ({
    key: localKey(),
    field,
    saveAs: str(saveAs),
  }));

const toOptions = options =>
  (Array.isArray(options) ? options : []).map(option => ({
    key: localKey(),
    source: rec(option),
    id: str(rec(option).id),
    labelEn: str(rec(option).labelEn),
    labelAr: str(rec(option).labelAr),
    next: str(rec(option).next),
  }));

/**
 * One stored step as a card edits it.
 *
 * A step whose `type` this builder does not know keeps `known: false` and is never
 * rewritten — see the header. Everything else is read into named fields, with `source`
 * held beside them for the fields that were not named.
 */
export const toStepRow = step => {
  const raw = rec(step);
  const type = str(raw.type);
  const row = {
    key: localKey(),
    source: raw,
    known: isKnownType(type),
    id: str(raw.id),
    name: str(raw.name),
    type,
    next: str(raw.next),
    clears: Array.isArray(raw.clears) ? raw.clears.filter(k => str(k)) : [],
    // Every type's fields are present on every row, blank where they do not apply. A card
    // renders only its own, and `stepToWire` writes only its own — but a switch from
    // `ask` to `choose` and back must not have silently emptied the instruction in
    // between, which is what a row that carried only the current type's fields would do.
    instruction: str(raw.instruction),
    kind: ANSWER_KINDS.includes(raw.kind) ? raw.kind : 'text',
    rule: toRule(raw.rule),
    saveTo: toSaveTo(raw.saveTo),
    options: toOptions(raw.options),
    connection: str(raw.connection),
    inputs: toInputs(raw.inputs),
    listPath: str(raw.listPath),
    labelField: str(raw.labelField),
    valueField: str(raw.valueField),
    format: LIVE_FORMATS.includes(raw.format) ? raw.format : 'text',
    ifNone: str(raw.ifNone),
    keep: toKeep(raw.keep),
    onWorked: str(raw.onWorked),
    onFailed: str(raw.onFailed),
    exactEn: str(raw.exactEn),
    exactAr: str(raw.exactAr),
    // `tell` has two arms and `.strict()` on each, so "which arm" is a real setting and
    // not something to infer from which box happens to be empty.
    exact: typeof raw.exactEn === 'string' || typeof raw.exactAr === 'string',
    note: str(raw.note),
  };
  return row;
};

/**
 * A stored definition as the cards edit it, or `null` for a task that has none.
 *
 * `null` in, `null` out, deliberately: "this task has no steps yet" is the state the old
 * free-text editor exists for, and it must never be confused with an empty graph.
 */
export const toFlowDraft = definition => {
  if (!definition || typeof definition !== 'object') return null;
  const steps = Array.isArray(definition.steps) ? definition.steps : [];
  // Top-level fields we do not know about ride along the same way a step's do.
  const { firstStepId, steps: _steps, ...rest } = definition;
  return {
    rest,
    firstStepId: str(firstStepId),
    steps: steps.map(toStepRow),
  };
};

// ── writing a definition out ──────────────────────────────────────────────────────────

/** Set a key, or remove it — an absent optional field and an empty one are not the same. */
const put = (target, key, value) => {
  if (value === undefined) delete target[key];
  else target[key] = value;
};

const trimmed = value => (value ?? '').trim();
const orUndefined = value =>
  trimmed(value) === '' ? undefined : trimmed(value);

const ruleToWire = rule => {
  if (rule.mode === 'range') {
    const min = Number(rule.min);
    const max = Number(rule.max);
    if (rule.min === '' || rule.max === '') return undefined;
    if (!Number.isFinite(min) || !Number.isFinite(max)) return undefined;
    return { min, max };
  }
  if (rule.mode === 'allowed') {
    const allowed = rule.allowed.map(trimmed).filter(Boolean);
    return allowed.length ? { allowed } : undefined;
  }
  if (rule.mode === 'pattern') {
    const pattern = trimmed(rule.pattern);
    return pattern ? { pattern } : undefined;
  }
  return undefined;
};

/**
 * `{ chat?, contact? }` — and `undefined` when neither box is filled.
 *
 * The schema has no arm for a target that saves nowhere, so sending `{}` would be refused
 * with a message about a union. The card says it in words instead.
 */
const saveToWire = saveTo => {
  const chat = orUndefined(saveTo.chat);
  const contact = orUndefined(saveTo.contact);
  if (chat === undefined && contact === undefined) return undefined;
  const out = {};
  if (chat !== undefined) out.chat = chat;
  if (contact !== undefined) out.contact = contact;
  return out;
};

/**
 * A row with no source is not an input — it is a box the owner has not filled in yet, and
 * `{ key: '' }` would be refused by the schema with a message about a union. The card
 * keeps the row so the picker stays on screen; the wire simply does not carry it.
 */
const inputsToWire = rows => {
  const out = {};
  rows.forEach(row => {
    const name = trimmed(row.name);
    if (!name) return;
    if (row.mode === 'key') {
      const from = trimmed(row.from);
      if (from) out[name] = { key: from };
      return;
    }
    // F2.3: the chat's language has nothing to type; Waslo fills it in when it calls.
    if (row.mode === 'lang') {
      out[name] = { lang: true };
      return;
    }
    out[name] = { value: row.value ?? '' };
  });
  return out;
};

/** Same rule as `inputsToWire`: a half-written row is a box, not a thing to keep. */
const keepToWire = rows => {
  const out = {};
  rows.forEach(row => {
    const field = trimmed(row.field);
    const saveAs = trimmed(row.saveAs);
    if (!field || !saveAs) return;
    out[field] = saveAs;
  });
  return out;
};

const optionToWire = row => {
  const wire = { ...row.source };
  wire.id = trimmed(row.id);
  wire.labelEn = row.labelEn ?? '';
  wire.labelAr = row.labelAr ?? '';
  put(wire, 'next', orUndefined(row.next));
  return wire;
};

/** Field names this builder manages, per type — everything else on `source` is kept. */
const OWNED = {
  ask: [
    'instruction',
    'kind',
    'rule',
    'saveTo',
    'phoneCountries',
    'phoneLines',
    'uploads',
  ],
  choose: ['instruction', 'options', 'saveTo'],
  choose_live: [
    'instruction',
    'connection',
    'inputs',
    'listPath',
    'labelField',
    'format',
    'valueField',
    'saveTo',
    'ifNone',
  ],
  call: ['connection', 'inputs', 'keep', 'onWorked', 'onFailed'],
  tell: ['instruction', 'exactEn', 'exactAr'],
  // Task review item 11: who gets the chats, and the labels. Owned so a step that stops being
  // a hand-off sheds them (the sidecar's schema is strict); written by the step screen.
  handoff: ['note', 'handTo', 'labels'],
  end: ['instruction'],
};

/** Every field any type owns — what a card must clear when its step changes type. */
const ALL_OWNED = [...new Set(Object.values(OWNED).flat())];

/**
 * One row, back in the shape the sidecar parses.
 *
 * A step of an unknown type is returned EXACTLY as it arrived. It is the only honest
 * answer: this builder cannot name its fields, so it cannot decide which of them are
 * stale, and a step it half-understands is worse than one it does not touch.
 */
export const stepToWire = row => {
  if (!row.known) return { ...row.source };

  const wire = { ...row.source };
  // Anything belonging to the type this step USED to be. A row that changed type keeps
  // its unknown fields (they may be ours from a newer sidecar) but never the old arm's.
  ALL_OWNED.filter(field => !(OWNED[row.type] ?? []).includes(field)).forEach(
    field => delete wire[field]
  );

  wire.id = trimmed(row.id);
  wire.name = trimmed(row.name);
  wire.type = row.type;
  put(wire, 'next', orUndefined(row.next));
  put(wire, 'clears', row.clears.length ? [...row.clears] : undefined);

  if (row.type === 'ask') {
    wire.instruction = trimmed(row.instruction);
    wire.kind = row.kind;
    put(wire, 'rule', ruleToWire(row.rule));
    put(wire, 'saveTo', saveToWire(row.saveTo));
  } else if (row.type === 'choose') {
    wire.instruction = trimmed(row.instruction);
    wire.options = row.options.map(optionToWire);
    put(wire, 'saveTo', saveToWire(row.saveTo));
  } else if (row.type === 'choose_live') {
    wire.instruction = trimmed(row.instruction);
    wire.connection = trimmed(row.connection);
    wire.inputs = inputsToWire(row.inputs);
    wire.listPath = trimmed(row.listPath);
    wire.labelField = trimmed(row.labelField);
    wire.format = row.format;
    wire.valueField = trimmed(row.valueField);
    put(wire, 'saveTo', saveToWire(row.saveTo));
    wire.ifNone = trimmed(row.ifNone);
  } else if (row.type === 'call') {
    wire.connection = trimmed(row.connection);
    wire.inputs = inputsToWire(row.inputs);
    wire.keep = keepToWire(row.keep);
    put(wire, 'onWorked', orUndefined(row.onWorked));
    put(wire, 'onFailed', orUndefined(row.onFailed));
  } else if (row.type === 'tell') {
    if (row.exact) {
      delete wire.instruction;
      wire.exactEn = row.exactEn ?? '';
      wire.exactAr = row.exactAr ?? '';
    } else {
      delete wire.exactEn;
      delete wire.exactAr;
      wire.instruction = trimmed(row.instruction);
    }
  } else if (row.type === 'handoff') {
    wire.note = trimmed(row.note);
  } else if (row.type === 'end') {
    put(wire, 'instruction', orUndefined(row.instruction));
  }
  return wire;
};

/** The draft as the BFF and the sidecar read it, or `null` for a task with no steps. */
export const flowDraftToWire = draft => {
  if (!draft) return null;
  return {
    ...draft.rest,
    firstStepId: trimmed(draft.firstStepId),
    steps: draft.steps.map(stepToWire),
  };
};

// ── making new things ─────────────────────────────────────────────────────────────────

/** The next free `s<n>`, so ids stay short, legal and readable in a branch picker. */
export const mintStepId = (taken, prefix = 's') => {
  const used = new Set(taken);
  let n = 1;
  while (used.has(`${prefix}${n}`)) n += 1;
  return `${prefix}${n}`;
};

export const newOption = (taken = []) => ({
  key: localKey(),
  source: {},
  id: mintStepId(taken, 'o'),
  labelEn: '',
  labelAr: '',
  next: '',
});

/** A blank step of one type, ready to be filled in. */
export const newStep = (type, taken = []) => {
  const row = toStepRow({ id: mintStepId(taken), name: '', type });
  if (type === 'choose') row.options = [newOption([]), newOption(['o1'])];
  if (type === 'tell') row.exact = false;
  return row;
};

/**
 * The definition a task gets when its owner presses "Write it as steps".
 *
 * ONE step, seeded from the FIRST free-text step — not all of them. A free-text step is a
 * sentence to a model ("Read the total and the delivery day back, and wait for a yes"), and
 * there is no honest mapping from that to a type, a save key and a branch. Converting all
 * of them would produce a graph that looks finished and is wrong in five places; one step
 * produces a graph that is obviously a beginning. The old steps are not destroyed — they
 * stay on the task's `steps` column until the owner is done.
 */
export const seedDefinition = (steps = []) => {
  const first = (Array.isArray(steps) ? steps : []).find(
    step => trimmed(step) !== ''
  );
  return {
    firstStepId: 's1',
    steps: [
      {
        id: 's1',
        name: '',
        type: 'ask',
        instruction: trimmed(first) || '',
        kind: 'text',
        saveTo: { chat: 'answer' },
      },
    ],
  };
};

// ── problems ──────────────────────────────────────────────────────────────────────────

/**
 * The graph rules, mirrored from `services/ai-sidecar/src/tasks/validate.ts`.
 *
 * Mirrored, not reimplemented differently: the two must agree or the builder shows a clean
 * page for a definition the server then refuses. The server stays authoritative — these run
 * on the wire shape so they see exactly what it will see.
 *
 * Returns the sidecar's own `Problem` shape: `{ stepId, problem, detail? }`.
 */
export const outgoing = step => {
  const out = [];
  if (typeof step.next === 'string' && step.next) out.push(step.next);
  if (step.type === 'choose') {
    (step.options ?? []).forEach(o => {
      if (typeof o.next === 'string' && o.next) out.push(o.next);
    });
  }
  if (step.type === 'choose_live' && step.ifNone) out.push(step.ifNone);
  if (step.type === 'call') {
    if (step.onWorked) out.push(step.onWorked);
    if (step.onFailed) out.push(step.onFailed);
    // P-10.21: "If your system answers" is a link too.
    (step.answers?.cases ?? []).forEach(c => {
      if (typeof c?.next === 'string' && c.next) out.push(c.next);
    });
  }
  return out;
};

/**
 * The key one answer is stored under, in the sidecar's own spelling (`validate.ts`).
 *
 * `''` FOR A TARGET THAT SAVES NOWHERE, which the sidecar's version cannot return and this
 * one must: there the definition has been through `taskDefinitionSchema` and `saveTo` is
 * always present, whereas here it is a box an owner has not filled in yet. Returning
 * `` `contact:${undefined}` `` put the literal string `contact:undefined` into the set of
 * keys a `clears` entry is checked against — so a step that forgot nothing could pass the
 * check by naming a key nothing writes.
 */
export const saveKeyOf = target => {
  if (target?.chat !== undefined) return target.chat;
  if (target?.contact !== undefined) return `contact:${target.contact}`;
  return '';
};

export const targetOf = step => {
  const collects =
    step.type === 'ask' ||
    step.type === 'choose' ||
    step.type === 'choose_live';
  if (!collects) return null;
  return step.saveTo && typeof step.saveTo === 'object' ? step.saveTo : null;
};

export const savedKeysOf = step => {
  const target = targetOf(step);
  if (target) return [saveKeyOf(target)].filter(Boolean);
  return step.type === 'call' ? Object.values(step.keep ?? {}) : [];
};

/** The CHAT keys a step writes — a contact-only target cannot be read back as an input. */
const saves = step => {
  const target = targetOf(step);
  if (target) return target.chat === undefined ? [] : [target.chat];
  return step.type === 'call' ? Object.values(step.keep ?? {}) : [];
};

const reads = step => {
  if (step.type !== 'call' && step.type !== 'choose_live') return [];
  return Object.values(step.inputs ?? {}).flatMap(i =>
    i && typeof i === 'object' && 'key' in i ? [i.key] : []
  );
};

/**
 * Every step the task can actually arrive at, breadth-first from the first step.
 *
 * One function, used by both the "nothing leads here" check and the "what may this step send
 * on" walk, because two copies of a graph traversal is two chances for the builder to
 * disagree with itself about which steps exist.
 */
const reachableFrom = (byId, firstStepId) => {
  const reachable = new Set();
  const queue = byId.has(firstStepId) ? [firstStepId] : [];
  while (queue.length > 0) {
    const id = queue.shift();
    if (!reachable.has(id)) {
      reachable.add(id);
      const step = byId.get(id);
      if (step) {
        outgoing(step).forEach(target => {
          if (byId.has(target)) queue.push(target);
        });
      }
    }
  }
  return reachable;
};

/**
 * Which chat keys are guaranteed to exist on arrival at each step — the intersection over
 * every incoming edge, to a fixed point. `validate.ts` computes exactly this to decide
 * whether an input is used before it is asked; the builder computes it to decide what to
 * OFFER in the picker, so the commonest problem on a connection card cannot be created.
 *
 * @returns {Map<string, Set<string>>} keyed by step id; only reachable steps are present.
 */
export const arrivingKeys = definition => {
  const arriving = new Map();
  if (!definition) return arriving;
  const steps = Array.isArray(definition.steps) ? definition.steps : [];
  const byId = new Map(steps.map(s => [s.id, s]));

  const reachable = reachableFrom(byId, definition.firstStepId);

  const allKeys = new Set(steps.flatMap(saves));
  reachable.forEach(id =>
    arriving.set(
      id,
      id === definition.firstStepId ? new Set() : new Set(allKeys)
    )
  );
  for (let pass = 0; pass < steps.length + 1; pass += 1) {
    let changed = false;
    reachable.forEach(id => {
      const step = byId.get(id);
      if (!step) return;
      const leaving = new Set([...(arriving.get(id) ?? []), ...saves(step)]);
      outgoing(step).forEach(target => {
        if (!reachable.has(target) || target === definition.firstStepId) return;
        const current = arriving.get(target);
        if (!current) return;
        const next = new Set([...current].filter(k => leaving.has(k)));
        if (next.size !== current.size) {
          arriving.set(target, next);
          changed = true;
        }
      });
    });
    if (!changed) break;
  }
  return arriving;
};

/**
 * Which chat keys MAY exist on arrival at each step: the UNION over incoming edges, where
 * `arrivingKeys` takes the intersection (field report F3.7). A key in this set and not in that
 * one is saved on only some ways to the step, which the wiring screen names rather than leaving
 * silently absent from its list.
 *
 * @returns {Map<string, Set<string>>} keyed by step id; only reachable steps are present.
 */
export const arrivingKeysOnSomePath = definition => {
  const arriving = new Map();
  if (!definition) return arriving;
  const steps = Array.isArray(definition.steps) ? definition.steps : [];
  const byId = new Map(steps.map(s => [s.id, s]));
  const reachable = reachableFrom(byId, definition.firstStepId);
  reachable.forEach(id => arriving.set(id, new Set()));
  for (let pass = 0; pass < steps.length + 1; pass += 1) {
    let changed = false;
    reachable.forEach(id => {
      const step = byId.get(id);
      if (!step) return;
      const leaving = [...(arriving.get(id) ?? []), ...saves(step)];
      outgoing(step).forEach(target => {
        if (!reachable.has(target) || target === definition.firstStepId) return;
        const current = arriving.get(target);
        if (!current) return;
        const before = current.size;
        leaving.forEach(key => current.add(key));
        if (current.size !== before) changed = true;
      });
    });
    if (!changed) break;
  }
  return arriving;
};

// ── connections that change something (P-3.4) ───────────────────────────────────────
// The sidecar's `confirmedSteps` / `connectionProblems` (`tasks/validate.ts`), mirrored so the
// owner reads the rule before the round trip. The server still refuses the write.

/** The phone question's country choices, in the order the composer lists them (P-10.10). */
export const PHONE_COUNTRY_CHOICES = [
  'KW',
  'SA',
  'AE',
  'QA',
  'BH',
  'OM',
  'any',
];

const linksInto = definition => {
  const into = new Map();
  (definition?.steps ?? []).forEach(step => {
    const add = (to, confirms) => {
      if (typeof to !== 'string' || to === '') return;
      if (!into.has(to)) into.set(to, []);
      into.get(to).push({ from: step.id, confirms });
    };
    if (step.type === 'choose') {
      (step.options ?? []).forEach(option =>
        option.next ? add(option.next, 'yes') : add(step.next, 'no')
      );
    } else if (step.type === 'call') {
      add(step.next, 'if_confirmed');
      add(step.onWorked, 'if_confirmed');
      add(step.onFailed, 'if_confirmed');
      // P-10.21: a case confirms exactly as `onWorked` does.
      (step.answers?.cases ?? []).forEach(c => add(c?.next, 'if_confirmed'));
    } else if (step.type === 'tell') {
      add(step.next, 'if_confirmed');
    } else if (step.type === 'choose_live') {
      add(step.next, 'no');
      add(step.ifNone, 'no');
    } else {
      add(step.next, 'no');
    }
  });
  return into;
};

/** The steps a customer only reaches by having just answered a question. A least fixpoint. */
export const confirmedSteps = definition => {
  const steps = definition?.steps ?? [];
  const into = linksInto(definition);
  const confirmed = new Set();
  for (let pass = 0; pass <= steps.length; pass += 1) {
    let changed = false;
    steps.forEach(step => {
      if (step.id === definition.firstStepId || confirmed.has(step.id)) return;
      const links = into.get(step.id) ?? [];
      if (links.length === 0) return;
      const ok = links.every(
        link =>
          link.confirms === 'yes' ||
          (link.confirms === 'if_confirmed' && confirmed.has(link.from))
      );
      if (ok) {
        confirmed.add(step.id);
        changed = true;
      }
    });
    if (!changed) break;
  }
  return confirmed;
};

/** `unconfirmed_change` and `live_needs_lookup`, as the sidecar reports them. */
export const connectionProblems = (definition, changeConnections) => {
  if (!definition || !changeConnections || changeConnections.size === 0)
    return [];
  const confirmed = confirmedSteps(definition);
  const problems = [];
  (definition.steps ?? []).forEach(step => {
    if (
      step.type === 'call' &&
      changeConnections.has(step.connection) &&
      !confirmed.has(step.id)
    ) {
      problems.push({
        stepId: step.id,
        problem: 'unconfirmed_change',
        detail: step.connection,
      });
    }
    if (step.type === 'choose_live' && changeConnections.has(step.connection)) {
      problems.push({
        stepId: step.id,
        problem: 'live_needs_lookup',
        detail: step.connection,
      });
    }
  });
  return problems;
};

/**
 * For each change that is confirmed, the question whose "yes" lets it run, in the owner's
 * language: the Check screen's "Changes your system only after the customer answers: …".
 */
export const confirmedChangeNotes = (
  definition,
  changeConnections,
  lang = 'en'
) => {
  if (!definition || !changeConnections || changeConnections.size === 0)
    return [];
  const steps = definition.steps ?? [];
  const byId = new Map(steps.map(step => [step.id, step]));
  const into = linksInto(definition);
  const confirmed = confirmedSteps(definition);
  const questionOf = step => {
    const own = lang === 'ar' ? step?.questionAr : step?.questionEn;
    return (own ?? '').trim() || step?.name || '';
  };
  const notes = [];
  steps.forEach(step => {
    if (
      step.type !== 'call' ||
      !changeConnections.has(step.connection) ||
      !confirmed.has(step.id)
    ) {
      return;
    }
    // Walk back from the change to the nearest option whose own link is a yes.
    const seen = new Set();
    const queue = [step.id];
    let question = '';
    while (queue.length > 0 && question === '') {
      const id = queue.shift();
      if (!seen.has(id)) {
        seen.add(id);
        const links = into.get(id) ?? [];
        const yes = links.find(link => link.confirms === 'yes');
        if (yes) question = questionOf(byId.get(yes.from));
        else queue.push(...links.map(link => link.from));
      }
    }
    if (question !== '') notes.push({ stepId: step.id, question });
  });
  return notes;
};

/**
 * A key no owner can type (it starts with a space), standing in for "the phone question
 * has been answered" so `arrivingKeys` can carry it. `arrivingKeys` only tracks CHAT keys,
 * and a phone question may save to the customer alone, so its own key cannot be used.
 */
const PHONE_ASKED = ' phone asked';

const asksForPhone = step =>
  step.type === 'ask' &&
  step.kind === 'phone' &&
  step.saveTo?.contact === 'phone_number';

/** Every step reachable from `from`'s own links (`validate.ts` `reachedFrom`). */
const reachedFrom = (byId, from) => {
  const linksOf = step => [
    ...outgoing(step),
    ...(step.ifStuck?.action === 'goto' && step.ifStuck.next
      ? [step.ifStuck.next]
      : []),
  ];
  const seen = new Set();
  const queue = linksOf(from);
  while (queue.length > 0) {
    const id = queue.shift();
    if (!seen.has(id)) {
      seen.add(id);
      const step = byId.get(id);
      if (step) queue.push(...linksOf(step));
    }
  }
  return seen;
};

/**
 * Field report F1.5 (D-02): a question that saves to a key an EARLIER question on the same path
 * writes is skipped as answered and never asked (`validate.ts` `savesOverEarlier`).
 */
const savesOverEarlier = (steps, byId) => {
  const questions = steps.filter(step => targetOf(step));
  const out = [];
  questions.forEach(later => {
    const key = saveKeyOf(targetOf(later));
    if (!key || (later.clears ?? []).includes(key)) return;
    const earlier = questions.find(
      step =>
        step.id !== later.id &&
        saveKeyOf(targetOf(step)) === key &&
        reachedFrom(byId, step).has(later.id)
    );
    if (earlier) {
      out.push({
        stepId: later.id,
        problem: 'saves_over_earlier',
        detail: key,
      });
    }
  });
  return out;
};

/**
 * "Worth a look", never a block (P-2.2): the sidecar's `lintDefinition`, mirrored so the Check
 * screen can say it before the round trip. The SAME six kinds, the same rules, and the shared
 * fixture (`specs/fixtures/task-lint-cases.json`, byte-identical with the sidecar's) keeps the
 * two in step. Each is `{ stepId, problem, detail? }`, `problem` being the sidecar's `warning`.
 *
 *  - `failure_path_on_normal_path`: a Call's "if it failed" (or a live list's "if nothing came
 *    back") is a message or a hand-over that success ALSO reaches.
 *  - `ends_silently_after_change`: a change is followed by the end with nothing said.
 *  - `typed_number_lookup` (P-10.10): a lookup keyed by a detail the customer types.
 *  - `kept_for_chat_only` (P-10.23): an email, a phone or a name kept for this chat only.
 *  - `connection_off` (P-10.21): a step uses a connection that is switched off or gone. Only
 *    when `ctx.enabled` is known, so a page still loading its connections warns about nothing.
 *  - `saves_over_earlier` (field report F1.5, D-02): a question saves where an earlier question
 *    on the same path already does, so it is never asked.
 *  - `username_needs_phone` (P-3.7, fork only): a CHANGE that sends this chat's WhatsApp number,
 *    reached by some way with no phone question saving to the customer's phone before it. A
 *    customer who messages from a WhatsApp username has no number, the system is then never
 *    called, and the task takes its "if it failed" way. The contact pre-fill already skips that
 *    question for a customer whose number is known. Only when `ctx.contactDetails` is given.
 *
 * @param {{ purposes?: Record<string, string>, params?: Record<string, Array<{name: string, description?: string}> | null>, enabled?: Record<string, boolean>, contactDetails?: Record<string, string[]> }} ctx
 */
export const graphWarnings = (definition, ctx = {}) => {
  const purposes = ctx.purposes ?? {};
  const params = ctx.params ?? {};
  const steps = definition?.steps ?? [];
  const byId = new Map(steps.map(step => [step.id, step]));
  const warnings = [];

  const normalLinks = step => {
    const out = [];
    if (step.next) out.push(step.next);
    if (step.type === 'choose') {
      (step.options ?? []).forEach(o => {
        if (o.next) out.push(o.next);
      });
    }
    if (step.type === 'call') {
      if (step.onWorked) out.push(step.onWorked);
      (step.answers?.cases ?? []).forEach(c => {
        if (c?.next) out.push(c.next);
      });
    }
    return out;
  };
  const normal = new Set();
  const queue = byId.has(definition?.firstStepId)
    ? [definition.firstStepId]
    : [];
  while (queue.length > 0) {
    const id = queue.shift();
    if (!normal.has(id)) {
      normal.add(id);
      const step = byId.get(id);
      if (step) queue.push(...normalLinks(step).filter(t => byId.has(t)));
    }
  }

  const successTargets = step => [
    step.onWorked || step.next || undefined,
    ...(step.answers?.cases ?? []).map(c => c?.next),
  ];
  const saysOnWorked = step =>
    Boolean(step.sayResult) && step.sayResult.on !== 'failed';
  const silent = (target, seen) => {
    if (target === undefined || target === '') return true;
    if (seen.has(target)) return false;
    seen.add(target);
    const step = byId.get(target);
    if (!step) return false;
    if (step.type === 'end') return (step.instruction ?? '').trim() === '';
    if (step.type === 'call') {
      if (saysOnWorked(step)) return false;
      return successTargets(step).some(t => silent(t, seen));
    }
    return false;
  };
  const typedKeys = new Set(
    steps
      .filter(step => step.type === 'ask' && step.saveTo?.chat !== undefined)
      .map(step => step.saveTo.chat)
  );

  steps.forEach(step => {
    if (step.type === 'call' || step.type === 'choose_live') {
      const failure = step.type === 'call' ? step.onFailed : step.ifNone;
      const target = failure ? byId.get(failure) : undefined;
      if (
        target &&
        (target.type === 'tell' || target.type === 'handoff') &&
        normal.has(target.id)
      ) {
        warnings.push({
          stepId: step.id,
          problem: 'failure_path_on_normal_path',
          detail: target.id,
        });
      }
      if (ctx.enabled !== undefined && ctx.enabled[step.connection] !== true) {
        warnings.push({
          stepId: step.id,
          problem: 'connection_off',
          detail: step.connection,
        });
      }
      if (purposes[step.connection] === 'lookup') {
        const declared = Array.isArray(params[step.connection])
          ? params[step.connection]
          : [];
        Object.entries(step.inputs ?? {}).forEach(([name, source]) => {
          if (
            !source ||
            typeof source.key !== 'string' ||
            !typedKeys.has(source.key)
          )
            return;
          const note =
            declared.find(param => param.name === name)?.description ?? '';
          if (IDENTITY_PARAM.test(name) || IDENTITY_PARAM.test(note)) {
            warnings.push({
              stepId: step.id,
              problem: 'typed_number_lookup',
              detail: name,
            });
          }
        });
      }
    }
    if (
      step.type === 'call' &&
      purposes[step.connection] === 'change' &&
      !saysOnWorked(step) &&
      successTargets(step).some(t => silent(t, new Set([step.id])))
    ) {
      warnings.push({ stepId: step.id, problem: 'ends_silently_after_change' });
    }
    if (
      step.type === 'ask' &&
      step.saveTo?.contact === undefined &&
      typeof step.saveTo?.chat === 'string'
    ) {
      const key = step.saveTo.chat;
      const named =
        step.kind === 'text' && (key === 'name' || key.endsWith('_name'));
      if (step.kind === 'email' || step.kind === 'phone' || named) {
        warnings.push({
          stepId: step.id,
          problem: 'kept_for_chat_only',
          detail: key,
        });
      }
    }
  });

  const contactDetails = ctx.contactDetails ?? {};
  // Each phone question saves the stand-in key, so "answered on every way in" is the same
  // intersection-over-incoming-edges walk the input pickers already use.
  const arriving = arrivingKeys({
    ...definition,
    steps: steps.map(step =>
      asksForPhone(step) ? { ...step, saveTo: { chat: PHONE_ASKED } } : step
    ),
  });
  steps.forEach(step => {
    if (step.type !== 'call' || purposes[step.connection] !== 'change') return;
    const sends = contactDetails[step.connection];
    if (!Array.isArray(sends) || !sends.includes('contact.phone_number'))
      return;
    if (!arriving.has(step.id)) return;
    if (arriving.get(step.id).has(PHONE_ASKED)) return;
    warnings.push({
      stepId: step.id,
      problem: 'username_needs_phone',
      detail: step.connection,
    });
  });
  warnings.push(...savesOverEarlier(steps, byId));
  return warnings;
};

/** The contact field a `kept_for_chat_only` fix saves an answer to, by the question's kind. */
export const profileFieldFor = step => {
  if (step?.kind === 'email') return 'email';
  if (step?.kind === 'phone') return 'phone_number';
  return 'name';
};

/**
 * The connection a question's answer is sent to, or `''` (`validate.ts` `connectionFedBy`).
 * T-16: skipping such an answer would send that system an empty one.
 */
export const connectionFedBy = (definition, step) => {
  const chat = targetOf(step)?.chat;
  if (!chat) return '';
  const reader = (definition?.steps ?? []).find(
    s =>
      (s.type === 'call' || s.type === 'choose_live') &&
      Object.values(s.inputs ?? {}).some(input => input?.key === chat)
  );
  return reader?.connection ?? '';
};

/**
 * P-10.25 and field report F1.3: "Skip this question" and the "Optional" box are refused where a
 * connection is sent the answer (`validate.ts` `skipProblems`, mirrored so the card says it
 * before the save does).
 */
export const skipProblems = definition => {
  const problems = [];
  (definition?.steps ?? []).forEach(step => {
    if (!targetOf(step)) return;
    const skips = step.optional === true || step.ifStuck?.action === 'skip';
    if (!skips) return;
    const fed = connectionFedBy(definition, step);
    if (fed) {
      problems.push({
        stepId: step.id,
        problem: 'skip_feeds_connection',
        detail: fed,
      });
    }
  });
  return problems;
};

/**
 * P-10.2, as the sidecar reports it (`read_back_not_linked`): a Yes/No that reads back a step
 * which is not a Call its yes can reach, or a message that summarises a step which is not a Call.
 * Services task review E, as `validate.ts` reports them: a read-back's kept value that no Call
 * keeps (`unknown_kept_key`), and kept values on a question that reads nothing back
 * (`keep_without_read_back`).
 */
export const readBackProblems = definition => {
  const steps = definition?.steps ?? [];
  const byId = new Map(steps.map(s => [s.id, s]));
  const keptByCalls = new Set(
    steps.flatMap(s => (s.type === 'call' ? Object.values(s.keep ?? {}) : []))
  );
  const reaches = (from, target) => {
    const seen = new Set();
    const queue = [...from];
    while (queue.length > 0) {
      const id = queue.shift();
      if (!seen.has(id)) {
        seen.add(id);
        if (id === target) return true;
        const step = byId.get(id);
        if (step) queue.push(...outgoing(step));
      }
    }
    return false;
  };
  const problems = [];
  steps.forEach(step => {
    if (step.type === 'choose' && step.readBackOf) {
      const starts = (step.options ?? [])
        .map(o => o.next || step.next)
        .filter(Boolean);
      if (
        byId.get(step.readBackOf)?.type !== 'call' ||
        !reaches(starts, step.readBackOf)
      ) {
        problems.push({
          stepId: step.id,
          problem: 'read_back_not_linked',
          detail: step.readBackOf,
        });
      }
    }
    // Services task review E: what an earlier Call kept, below the read-back.
    const kept = Array.isArray(step.readBackKeep) ? step.readBackKeep : [];
    if (step.type === 'choose' && kept.length > 0) {
      if (!step.readBackOf) {
        problems.push({ stepId: step.id, problem: 'keep_without_read_back' });
      }
      kept.forEach(entry => {
        if (!keptByCalls.has(entry?.key)) {
          problems.push({
            stepId: step.id,
            problem: 'unknown_kept_key',
            detail: entry?.key,
          });
        }
      });
    }
    if (
      step.type === 'tell' &&
      step.summaryOf &&
      byId.get(step.summaryOf)?.type !== 'call'
    ) {
      problems.push({
        stepId: step.id,
        problem: 'read_back_not_linked',
        detail: step.summaryOf,
      });
    }
  });
  return problems;
};

export const graphProblems = (definition, ctx = {}) => {
  const problems = [];
  if (!definition) return problems;
  const steps = Array.isArray(definition.steps) ? definition.steps : [];
  const byId = new Map(steps.map(s => [s.id, s]));

  if (!byId.has(definition.firstStepId)) {
    problems.push({
      stepId: definition.firstStepId,
      problem: 'missing_first',
    });
  }

  const written = new Set(steps.flatMap(savedKeysOf));

  steps.forEach(step => {
    outgoing(step).forEach(target => {
      if (!byId.has(target)) {
        problems.push({ stepId: step.id, problem: 'dangling', detail: target });
      }
    });
    (step.clears ?? []).forEach(key => {
      if (!written.has(key)) {
        problems.push({
          stepId: step.id,
          problem: 'unknown_clear',
          detail: key,
        });
      }
    });
    if (step.type === 'choose') {
      const seen = new Set();
      (step.options ?? []).forEach(o => {
        [(o.labelEn ?? '').trim(), (o.labelAr ?? '').trim()].forEach(label => {
          if (label === '') return;
          if (seen.has(label)) {
            problems.push({
              stepId: step.id,
              problem: 'duplicate_label',
              detail: label,
            });
          }
          seen.add(label);
        });
      });
    }
  });

  const reachable = reachableFrom(byId, definition.firstStepId);
  steps.forEach(step => {
    if (!reachable.has(step.id)) {
      problems.push({ stepId: step.id, problem: 'unreachable' });
    }
  });

  // A key saved on only ONE branch of a choose is a blank parameter on the other, which
  // reaches the owner's own system as an empty string rather than as an error. Computed
  // once, in `arrivingKeys`, because the pickers offer exactly the set this check enforces.
  const arriving = arrivingKeys(definition);
  reachable.forEach(id => {
    const step = byId.get(id);
    if (!step) return;
    const available = arriving.get(id) ?? new Set();
    reads(step).forEach(key => {
      if (!available.has(key)) {
        problems.push({
          stepId: step.id,
          problem: 'input_before_save',
          detail: key,
        });
      }
    });
  });

  // P-3.4: only when the page knows which connections change something.
  problems.push(...connectionProblems(definition, ctx.changeConnections));
  // P-2.10: a one-language account's options need words in that language
  // (`tasks/languages.ts`, mirrored). Absent is Both, which adds nothing.
  problems.push(...optionLanguageProblems(definition, ctx.customerLanguages));
  problems.push(...readBackProblems(definition));
  problems.push(...skipProblems(definition));

  return problems;
};

/**
 * The per-step rules `taskDefinitionSchema` would refuse, said in the owner's words and
 * BEFORE the round trip. Same `Problem` shape, so a card renders both lists identically.
 */
export const shapeProblems = definition => {
  const problems = [];
  if (!definition) return problems;
  const steps = Array.isArray(definition.steps) ? definition.steps : [];
  const at = (step, problem, detail) =>
    problems.push({ stepId: step.id, problem, ...(detail ? { detail } : {}) });

  if (steps.length > MAX_GRAPH_STEPS) {
    problems.push({
      stepId: steps[MAX_GRAPH_STEPS]?.id ?? '',
      problem: 'too_many_steps',
    });
  }

  const seenIds = new Set();
  steps.forEach(step => {
    if (!isKnownType(step.type)) return;
    if (!STEP_ID.test(step.id ?? '')) at(step, 'bad_step_id');
    if (seenIds.has(step.id)) at(step, 'duplicate_id', step.id);
    seenIds.add(step.id);
    if ((step.name ?? '').trim() === '') at(step, 'blank_name');
    else if ((step.name ?? '').length > MAX_NAME) at(step, 'name_long');

    const needsInstruction =
      step.type === 'ask' ||
      step.type === 'choose' ||
      step.type === 'choose_live' ||
      (step.type === 'tell' && step.exactEn === undefined);
    if (needsInstruction && (step.instruction ?? '').trim() === '') {
      at(step, 'blank_instruction');
    }
    if ((step.instruction ?? '').length > MAX_INSTRUCTION) {
      at(step, 'instruction_long');
    }

    if (
      step.type === 'ask' ||
      step.type === 'choose' ||
      step.type === 'choose_live'
    ) {
      if (step.saveTo === undefined) at(step, 'no_save_target');
      else if (
        step.saveTo.chat !== undefined &&
        !SAVE_KEY.test(step.saveTo.chat)
      ) {
        at(step, 'bad_save_key', step.saveTo.chat);
      }
    }

    if (step.type === 'choose') {
      const options = step.options ?? [];
      if (options.length === 0) at(step, 'no_options');
      if (options.length > optionCapOf(step.many)) at(step, 'too_many_options');
      options.forEach(option => {
        if (
          (option.labelEn ?? '').trim() === '' &&
          (option.labelAr ?? '').trim() === ''
        ) {
          at(step, 'option_unlabelled');
        }
        [option.labelEn, option.labelAr].forEach(label => {
          if ((label ?? '').length > MAX_OPTION_LABEL) {
            at(step, 'label_long', (label ?? '').trim());
          }
        });
      });
    }

    if (step.type === 'choose_live' || step.type === 'call') {
      if ((step.connection ?? '').trim() === '') at(step, 'no_connection');
      Object.entries(step.inputs ?? {}).forEach(([name, source]) => {
        if ('key' in source && !SAVE_KEY.test(source.key)) {
          at(step, 'bad_input', name);
        }
      });
    }
    if (step.type === 'choose_live') {
      if ((step.listPath ?? '').trim() === '') at(step, 'no_list_path');
      if ((step.labelField ?? '').trim() === '') at(step, 'no_label_field');
      if ((step.valueField ?? '').trim() === '') at(step, 'no_value_field');
      if ((step.ifNone ?? '').trim() === '') at(step, 'no_if_none');
    }
    if (step.type === 'call') {
      Object.entries(step.keep ?? {}).forEach(([field, saveAs]) => {
        if (!SAVE_KEY.test(saveAs)) at(step, 'bad_keep_key', field);
      });
    }
    if (step.type === 'tell') {
      if (step.exactEn !== undefined) {
        if (
          (step.exactEn ?? '').trim() === '' &&
          (step.exactAr ?? '').trim() === ''
        ) {
          at(step, 'blank_exact');
        }
        if (
          (step.exactEn ?? '').length > MAX_EXACT ||
          (step.exactAr ?? '').length > MAX_EXACT
        ) {
          at(step, 'exact_long');
        }
      }
      // P-4.1 / P-4.2: what it sends with its words, as the sidecar's schema will judge it.
      const send = step.send;
      if (send?.kind === 'link') {
        if (
          (send.labelEn ?? '').trim() === '' &&
          (send.labelAr ?? '').trim() === ''
        ) {
          at(step, 'link_unlabelled');
        }
        if (
          (send.labelEn ?? '').length > MAX_LINK_LABEL ||
          (send.labelAr ?? '').length > MAX_LINK_LABEL
        ) {
          at(step, 'link_label_long');
        }
        const url = send.url ?? {};
        if (
          'key' in url
            ? !SAVE_KEY.test(url.key ?? '')
            : !isSendableLink(url.value)
        ) {
          at(step, 'link_invalid');
        }
      }
      if (
        send?.kind === 'location' &&
        !(Number.isInteger(send.placeId) && send.placeId > 0)
      ) {
        at(step, 'no_place');
      }
    }
    if (step.type === 'handoff' && (step.note ?? '').trim() === '') {
      at(step, 'blank_note');
    }
    // Task review item 11: the sidecar's `tasks/hand-to.ts`, restated (`helpers/hand-to.js`).
    if (step.type === 'handoff' && !validHandTo(step.handTo)) {
      at(step, 'bad_hand_to');
    }
    if (
      step.type === 'handoff' &&
      step.labels !== undefined &&
      !validHandoverLabels(step.labels)
    ) {
      at(step, 'bad_handoff_labels');
    }
  });

  return problems;
};

/**
 * Every answer this task collects, as the "forget these" chooser offers them.
 *
 * IT IS THE SET `unknown_clear` IS CHECKED AGAINST, deliberately: `validate.ts` computes it
 * as `def.steps.flatMap(savedKeysOf)`, and offering exactly that set is what makes the
 * problem unreachable from the builder instead of merely reportable. A free-text list of
 * save keys would let an owner name one that does not exist, which forgets nothing and says
 * so to nobody — the reason that problem kind exists.
 *
 * The step that WRITES each key comes with it, because a key on its own (`start_hour`) does
 * not tell an owner which question she is about to un-ask.
 *
 * @returns {{key, name, onContact, stepId, stepName, stepNumber}[]} in graph order, deduped.
 */
export const writtenKeys = definition => {
  const out = [];
  if (!definition) return out;
  const steps = Array.isArray(definition.steps) ? definition.steps : [];
  const seen = new Set();
  steps.forEach((step, index) => {
    savedKeysOf(step).forEach(key => {
      if (!key || seen.has(key)) return;
      seen.add(key);
      const onContact = key.startsWith('contact:');
      out.push({
        key,
        name: onContact ? key.slice('contact:'.length) : key,
        onContact,
        stepId: step.id,
        stepName: typeof step.name === 'string' ? step.name : '',
        stepNumber: index + 1,
      });
    });
  });
  return out;
};

/** Everything this builder can tell an owner without asking the server. */
export const localProblems = (definition, ctx = {}) => [
  ...shapeProblems(definition),
  ...graphProblems(definition, ctx),
];

/**
 * A `Problem` from either side of the wire, in one shape.
 *
 * THE REAL SHAPE IS `validate.ts`'s: `{ stepId, problem: ProblemKind, detail?: string }`.
 * `GRAPH-API-CONTRACT.md` §1 first printed it as `{kind, stepId, message}` and was corrected
 * — so `problem`/`detail` is what arrives, and the `kind`/`message` fallback stays as one
 * line of insurance rather than as a belief about the server.
 *
 * TWO THINGS FOLLOW FROM THAT SHAPE AND BOTH ARE ON SCREEN:
 *   • `problem` IS A MACHINE TOKEN. There is no server prose to render. Every sentence an
 *     owner reads is ours, from the tables in `i18n.js` — `problemMessage` below.
 *   • `detail` IS OPTIONAL. A problem may carry a step id and a kind and nothing else, so
 *     every sentence has to read correctly without it and use it only as extra context.
 *     `''` here, never `undefined`, so a template cannot print "undefined".
 */
export const normaliseProblem = raw => {
  const row = rec(raw);
  const kind = str(row.problem) || str(row.kind);
  if (!kind) return null;
  return {
    stepId: str(row.stepId),
    problem: kind,
    detail: str(row.detail) || str(row.message) || '',
  };
};

export const normaliseProblems = raw =>
  (Array.isArray(raw) ? raw : []).map(normaliseProblem).filter(Boolean);

/** Problems keyed by the step they name, so a card can render only its own. */
export const problemsByStep = problems => {
  const out = {};
  problems.forEach(problem => {
    const id = problem.stepId || '';
    if (!out[id]) out[id] = [];
    out[id].push(problem);
  });
  return out;
};

/**
 * One problem as a sentence an owner can act on.
 *
 * EVERY SENTENCE IS OURS. The server sends a token — `unreachable`, `input_before_save` —
 * and a token is not copy: "unreachable" names a property of a graph, and the owner needs to
 * be told that nothing leads to this step so no customer will ever see it. So each kind has
 * two keys: one that reads correctly on its own, and one that uses `detail` when there is
 * some. `detail` is optional on the wire and a sentence built around it would print a blank
 * half the time.
 *
 * A kind this builder has never heard of still gets a sentence: the server is allowed to
 * grow new ones, and "there is something wrong with this step and we cannot say what" is
 * more use than a card that looks finished and will not save.
 *
 * @param {{problem: string, detail?: string}} problem
 * @param {(key: string, params?: object) => string} t
 */
const DETAILED_KINDS = new Set([
  'dangling',
  'input_before_save',
  'duplicate_label',
  'unknown_clear',
  'bad_save_key',
  'label_long',
  'bad_input',
  'bad_keep_key',
  'duplicate_id',
  'skip_feeds_connection',
  'unknown_link_key',
  'unknown_kept_key',
  'unknown_label',
]);

export const problemMessage = (problem, t) => {
  const base = `WASLO.TASKS.EDITOR.FLOW.PROBLEMS.${problem.problem.toUpperCase()}`;
  // P-2.10: `detail` is `<optionId>:<lang>`, a machine token. The sentence names the language.
  if (problem.problem === 'option_missing_label') {
    const lang = String(problem.detail ?? '')
      .split(':')
      .pop();
    return t(`${base}_${lang === 'ar' ? 'AR' : 'EN'}`);
  }
  // An allowlist rather than "try `_DETAIL` and see": `t()` on a key that is not in the
  // tables logs a warning, and asking for a variant that was never written for 20 of the 29
  // kinds would put 20 warnings in the console of a page that is working correctly.
  if (problem.detail && DETAILED_KINDS.has(problem.problem)) {
    return t(`${base}_DETAIL`, { detail: problem.detail });
  }
  const plain = t(base);
  if (plain !== base) return plain;
  return problem.detail
    ? t('WASLO.TASKS.EDITOR.FLOW.PROBLEMS.OTHER_DETAIL', {
        detail: problem.detail,
      })
    : t('WASLO.TASKS.EDITOR.FLOW.PROBLEMS.OTHER');
};

/**
 * One warning as a sentence with the OWNER's names in it. Every key is ours; the step and the
 * connection are looked up in the graph, never shown by id.
 */
export const warningMessage = (warning, definition, t) => {
  const byId = new Map((definition?.steps ?? []).map(step => [step.id, step]));
  const nameOf = id => (byId.get(id)?.name ?? '').trim() || id || '';
  const key = name => `WASLO.TASKS.COMPOSE.CHECK.WARN.${name}`;
  switch (warning.problem) {
    case 'failure_path_on_normal_path':
      return t(key('FAILURE_PATH'), {
        call: nameOf(warning.stepId),
        step: nameOf(warning.detail),
      });
    case 'ends_silently_after_change':
      return t(key('ENDS_SILENTLY'), { step: nameOf(warning.stepId) });
    case 'kept_for_chat_only':
      return t(key('KEPT_FOR_CHAT'), { step: nameOf(warning.stepId) });
    case 'connection_off':
      return t(key('CONNECTION_OFF'), { connection: warning.detail ?? '' });
    case 'saves_over_earlier':
      return t(key('SAVES_OVER'), { step: nameOf(warning.stepId) });
    default:
      return problemMessage(warning, t);
  }
};

// ── the preview ───────────────────────────────────────────────────────────────────────

/**
 * A step's choices in the shape `MenuPreview.vue` already draws, so the phone mock on a
 * card and the one on the Menus page are the same component counting the same limits.
 *
 * THE BUBBLE IS DELIBERATELY EMPTY. A step's `instruction` is written TO THE ASSISTANT,
 * not to the customer — it phrases the question itself, every time, in the customer's own
 * language. Drawing the instruction in the bubble would teach an owner that what she typed
 * is what gets sent, which is the single most expensive misunderstanding this product has.
 * The empty body renders the preview's own "Your assistant's reply appears here."
 */
export const stepPreviewDraft = (row, liveSample = []) => {
  const options =
    row.type === 'choose'
      ? row.options.map(option => ({
          id: option.id,
          titleEn: option.labelEn,
          titleAr: option.labelAr,
          taskId: null,
        }))
      : liveSample.map((sample, index) => ({
          id: `live${index}`,
          titleEn: sample,
          titleAr: sample,
          taskId: null,
        }));
  return {
    title: '',
    whenToShow: '',
    enabled: true,
    bodyEn: '',
    bodyAr: '',
    buttonEn: '',
    buttonAr: '',
    options,
  };
};

// ── live options, as the engine will build them (1c) ──────────────────────────────────

/**
 * Read a dotted path out of a test result — the one the owner typed, nothing clever. `$` is
 * the whole answer and a leading `$.` is dropped, exactly as the engine reads it (P-3.2,
 * `valueAt` in the sidecar's `tasks/effects.ts`).
 */
export const readPath = (value, path) => {
  if (!path) return undefined;
  if (path === '$') return value;
  const text = String(path);
  return (text.startsWith('$.') ? text.slice(2) : text)
    .split('.')
    .filter(Boolean)
    .reduce(
      (level, part) =>
        level && typeof level === 'object' ? level[part] : undefined,
      value
    );
};

/**
 * Every dotted path in a test result that holds a non-empty array of objects. An answer that
 * IS such a list is offered as `$`, "the whole answer" (P-3.2): slot and product systems often
 * answer with a bare list, and there was no path to point at.
 */
export const arrayPaths = (value, prefix = '', depth = 0, out = []) => {
  if (depth > 4 || !value || typeof value !== 'object') return out;
  if (Array.isArray(value)) {
    const first = value[0];
    if (
      depth === 0 &&
      first &&
      typeof first === 'object' &&
      !Array.isArray(first)
    ) {
      out.push('$');
    }
    return out;
  }
  Object.entries(value).forEach(([key, child]) => {
    const path = prefix ? `${prefix}.${key}` : key;
    if (Array.isArray(child)) {
      if (child.length > 0 && child[0] && typeof child[0] === 'object') {
        out.push(path);
      }
      return;
    }
    if (child && typeof child === 'object')
      arrayPaths(child, path, depth + 1, out);
  });
  return out;
};

/** The field names on the first row of a list — what the label and value pickers offer. */
export const fieldsAt = (value, listPath) => {
  const list = readPath(value, listPath);
  if (!Array.isArray(list) || list.length === 0) return [];
  const first = list[0];
  if (!first || typeof first !== 'object') return [];
  return Object.keys(first).filter(
    key => first[key] === null || typeof first[key] !== 'object'
  );
};

/**
 * A guess at which field is the label and which is the value, from the first row.
 *
 * It is only ever a starting point — the owner confirms both in the pickers. The value
 * guess leans towards an id because a value is INTERNAL and a label is not: offering the
 * customer a row id is the failure this whole screen is arranged to prevent.
 */
const LABEL_HINTS = [
  'label',
  'name',
  'title',
  'time',
  'slot',
  'display',
  'text',
];
const VALUE_HINTS = ['id', 'value', 'code', 'key', 'uuid', 'ref'];

const bestField = (fields, hints) => {
  const lower = fields.map(f => [f, f.toLowerCase()]);
  // An exact hit on any hint beats a partial hit on an earlier one: a field called `id` is
  // the value, and a field called `identifier_label` is not, however early `id` appears.
  const exact = hints
    .map(hint => lower.find(([, name]) => name === hint))
    .find(Boolean);
  if (exact) return exact[0];
  const partial = hints
    .map(hint => lower.find(([, name]) => name.includes(hint)))
    .find(Boolean);
  return partial ? partial[0] : '';
};

export const guessMapping = (value, listPath) => {
  const fields = fieldsAt(value, listPath);
  const valueField = bestField(fields, VALUE_HINTS);
  const labelField =
    bestField(
      fields.filter(f => f !== valueField),
      LABEL_HINTS
    ) ||
    fields.find(f => f !== valueField) ||
    '';
  return {
    fields,
    labelField,
    valueField: valueField || fields[0] || '',
  };
};

/**
 * What the buttons will say, formatted the way the engine formats them.
 *
 * Rough by design: the sidecar does the real formatting with the chat's language and time
 * zone. This exists so an owner can see that `2026-09-30T16:00:00Z` becomes something a
 * customer can read BEFORE she switches the task on, which is the whole reason the format
 * setting is on the card and not inferred.
 */
export const formatLiveLabel = (raw, format, locale = 'en') => {
  if (raw === null || raw === undefined) return '';
  const text = String(raw);
  if (format === 'text') return text;
  // The screen's locale as `Intl` takes it: Arabic digits for Arabic (O-5), and no throw on
  // Chatwoot's `pt_BR`.
  const intlLocale = toIntlLocale(locale);
  if (format === 'price') {
    const number = Number(text);
    if (!Number.isFinite(number)) return text;
    return `${new Intl.NumberFormat(intlLocale, {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    }).format(number)} KWD`;
  }
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return text;
  if (format === 'time') {
    return new Intl.DateTimeFormat(intlLocale, {
      hour: 'numeric',
      minute: '2-digit',
    }).format(date);
  }
  return new Intl.DateTimeFormat(intlLocale, {
    day: 'numeric',
    month: 'short',
  }).format(date);
};

/**
 * The first rows of a test result as the customer would see them.
 *
 * `MORE_AT` is the engine's rule (spec §3): past ten options WhatsApp gets the first nine
 * and a "More…" row, so the preview shows nine and says how many are behind it.
 */
export const MORE_AT = 10;
export const SHOWN_BEFORE_MORE = 9;

export const liveSample = (value, mapping, locale = 'en') => {
  const list = readPath(value, mapping.listPath);
  if (!Array.isArray(list)) {
    return { labels: [], total: 0, more: false, missingValue: false };
  }
  const rows = list
    .filter(row => row && typeof row === 'object')
    .map(row => ({
      label: formatLiveLabel(row[mapping.labelField], mapping.format, locale),
      value: row[mapping.valueField],
    }));
  const more = rows.length > MORE_AT;
  return {
    labels: (more ? rows.slice(0, SHOWN_BEFORE_MORE) : rows).map(r => r.label),
    // Kept so a card can say a row has no value without ever SHOWING one.
    missingValue: rows.some(
      r => r.value === undefined || r.value === null || r.value === ''
    ),
    total: rows.length,
    more,
  };
};

export default toFlowDraft;
